// Copiloto VaulTex (VLT-308 · v1 con datos reales del tenant).
// HONESTIDAD: el copiloto responde SOLO con datos de la firma (vía los
// servicios existentes, RLS intacta) usando intenciones deterministas —
// jamás improvisa ni da conclusiones fiscales. El lenguaje libre con LLM
// llega cuando exista la llave de API (misma interfaz). Reglas §7216:
// no toca datos de un contribuyente sin su consentimiento vigente.
// Auditoría: cada consulta queda en ai_conversations/ai_messages (RLS 0002).
import { supabase, hasBackend } from '@/lib/supabase';
import { clientsService } from '@/services/clients';
import { requestsService, pendingItems } from '@/services/requests';
import { returnsService } from '@/services/returns';
import { consentsService } from '@/services/consents';
import { aiService, type AiTurn } from '@/services/ai';
import { sessionUser } from '@/lib/sessionUser';

/** Historial para Loruel (VLT-384): las preguntas del usuario + las
 *  respuestas del propio Claude — así puede negociar en varios turnos
 *  ("¿para cuándo?" → "mañana a las 10") y agendar con contexto. */
export function toAiHistory(entries: { who: 'user' | 'copilot'; text?: string; answer?: CopilotAnswer }[]): AiTurn[] {
  const turns: AiTurn[] = [];
  for (const e of entries) {
    if (e.who === 'user' && e.text) turns.push({ role: 'user', content: e.text });
    else if (e.who === 'copilot' && e.answer?.kind === 'llm') turns.push({ role: 'assistant', content: e.answer.text });
  }
  return turns.slice(-10);
}

export interface CopilotSuggestion {
  id: string;
  labelKey: string;
  labelParams?: Record<string, string | number>;
  resultKey: string;
}

export interface CopilotBriefing {
  messageKey: string;
  messageParams: Record<string, string | number>;
  suggestions: CopilotSuggestion[];
}

/** Respuesta estructurada: la UI la compone localizada; sources = citas. */
export type CopilotAnswer =
  | { kind: 'missing'; clientName: string; clientId: string; items: { label: string; requestTitle: string }[] }
  | { kind: 'noConsent'; clientName: string; clientId: string }
  | { kind: 'clientNotFound'; query: string }
  | { kind: 'summary'; counts: Record<string, number> }
  // VLT-382: respuesta en lenguaje libre de Claude (vía Worker; sin datos de
  // clientes en esta fase). Los intents deterministas siguen siendo el suelo.
  | { kind: 'llm'; text: string; toolsUsed?: { name: string; ok: boolean }[]; masked?: number }
  | { kind: 'help' };

export interface CopilotService {
  getBriefing(): Promise<CopilotBriefing>;
  /** Responde una pregunta con datos reales del tenant (intents deterministas)
   *  o con Loruel (Claude) — `history` da memoria de la conversación. */
  ask(query: string, history?: AiTurn[]): Promise<CopilotAnswer>;
}

/** Detección de intención (pura, testeable): qué pide y sobre quién. */
export function detectIntent(query: string): { intent: 'missing' | 'summary' | 'help'; subject?: string } {
  const q = query.trim().toLowerCase();
  const es = q.match(/(?:qu[eé] )?le falta a\s+([\p{L}\s.'-]+?)\s*[?.!¿¡]*$/iu);
  if (es?.[1]) return { intent: 'missing', subject: es[1].trim() };
  const en = q.match(/what(?:'s| is)\s+([\p{L}\s.'-]+?)\s+missing\s*[?.!]*$/iu) ?? q.match(/missing for\s+([\p{L}\s.'-]+?)\s*[?.!]*$/iu);
  if (en?.[1]) return { intent: 'missing', subject: en[1].trim() };
  if (/(resumen|summary|c[oó]mo va|pipeline|hoy\b|today\b)/i.test(q)) return { intent: 'summary' };
  return { intent: 'help' };
}

/** Registro auditado de la consulta (fire-and-forget; no bloquea la respuesta). */
async function logInteraction(raw: string, answerKind: string, clientId?: string) {
  if (!hasBackend) return;
  // VLT-154: el rastro de la IA se guarda SIN datos sensibles (la base lo
  // vuelve a filtrar con mask_pii_text, 0071). Import diferido: el filtro no
  // pesa en la carga inicial (el copiloto vive en el armazón).
  const { maskPii } = await import('../../worker/piiMask.js');
  const question = maskPii(raw).text;
  try {
    const { data: auth } = await sessionUser();
    if (!auth.user) return;
    const { data: prof } = await supabase.from('profiles').select('organization_id').eq('id', auth.user.id).maybeSingle();
    const orgId = (prof as { organization_id: string } | null)?.organization_id;
    if (!orgId) return;
    const { data: conv } = await supabase
      .from('ai_conversations')
      .insert({ organization_id: orgId, profile_id: auth.user.id, client_id: clientId ?? null, title: question.slice(0, 80), model: 'vaultex-intents-v1' } as never)
      .select('id')
      .single();
    const convId = (conv as { id: string } | null)?.id;
    if (!convId) return;
    await supabase.from('ai_messages').insert([
      { organization_id: orgId, conversation_id: convId, role: 'user', content: question },
      { organization_id: orgId, conversation_id: convId, role: 'assistant', content: `[${answerKind}]` },
    ] as never);
  } catch {
    /* la auditoría de IA nunca rompe la respuesta */
  }
}

export const copilotService: CopilotService = {
  getBriefing: () =>
    Promise.resolve({
      messageKey: 'copilot.briefing',
      messageParams: { count: 3 },
      suggestions: [
        { id: 'missing', labelKey: 'copilot.suggestions.askMissing', resultKey: '' },
        { id: 'summary', labelKey: 'copilot.suggestions.daySummary', resultKey: '' },
      ],
    }),

  async ask(query, history = []) {
    const { intent, subject } = detectIntent(query);

    if (intent === 'summary') {
      const counts = await returnsService.pipeline();
      void logInteraction(query, 'summary');
      return { kind: 'summary', counts: counts as unknown as Record<string, number> };
    }

    if (intent === 'missing' && subject) {
      const matches = await clientsService.list(subject, { limit: 3 });
      const client = matches[0];
      if (!client) {
        void logInteraction(query, 'clientNotFound');
        return { kind: 'clientNotFound', query: subject };
      }
      // §7216: sin consentimiento vigente del contribuyente, la IA no procede.
      const consent = await consentsService.statusForClient(client.id, 'ai_processing');
      if (!consent.granted) {
        void logInteraction(query, 'noConsent', client.id);
        return { kind: 'noConsent', clientName: client.name, clientId: client.id };
      }
      const reqs = await requestsService.listForClient(client.id);
      const items = reqs.flatMap((r) =>
        pendingItems(r.items).map((it) => ({ label: it.label, requestTitle: r.title })),
      );
      void logInteraction(query, 'missing', client.id);
      return { kind: 'missing', clientName: client.name, clientId: client.id, items };
    }

    // VLT-382: lo que los intents no reconocen va a Claude (Worker /api/ai/chat).
    // Falla o demo → 'help' determinista: el copiloto nunca queda mudo.
    const llm = await aiService.chat([...history, { role: 'user', content: query }]);
    if (llm) {
      void logInteraction(query, 'llm');
      return { kind: 'llm', text: llm.text, toolsUsed: llm.toolsUsed, masked: llm.masked };
    }
    void logInteraction(query, 'help');
    return { kind: 'help' };
  },
};
